/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useFormikContext } from 'formik';
import {
  formControl,
  formInputText,
  formControlContainer,
  formControlError,
} from '@assets/styles/formStyles';
import Colors from '@global/colors';
import { isWeb, isAndroid } from '@global/screens';
import { INPUT_HEIGHT } from '@/constants';
import SociollaText from '@components/sociolla/SociollaText';
import ModalHalf from '@components/modal/ModalHalf';
import Button from '@components/form/Button';
import FormLabel from '@components/form/common/FormLabel';
import FormOverlay from '@components/form/common/FormOverlay';
import FormArrow from '@components/form/common/FormArrow';
import FormError from '@components/form/common/FormError';
import PropTypes from 'prop-types';

const propTypes = {
  testID: PropTypes.string,
  value: PropTypes.string,
  label: PropTypes.string,
  editable: PropTypes.bool,
  containerStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  inputContainerStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  placeholder: PropTypes.string,
  disableBeforeTime: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disableAfterTime: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isManualError: PropTypes.bool,
};

const defaultProps = {
  testID: 'time-picker',
  value: '',
  label: '',
  editable: true,
  containerStyle: {},
  inputContainerStyle: {},
  hasError: false,
  placeholder: '',
  disableBeforeTime: '',
  disableAfterTime: '',
  isManualError: false,
};

const hourOptions = [];
for (let i = 0; i < 24; i++) {
  const val = i.toString();
  hourOptions.push(val.length == 1 ? `0${i}` : val);
}
const minuteOptions = [];
for (let i = 0; i < 60; i++) {
  const val = i.toString();
  minuteOptions.push(val.length == 1 ? `0${i}` : val);
}

const TimePickerDefault = (props) => {
  const dataFormik = useFormikContext();
  const {
    testID = 'time-picker',
    value = '',
    label = '',
    editable = true,
    containerStyle = {},
    inputContainerStyle = {},
    hasError = false,
    placeholder = '',
    disableBeforeTime = '',
    disableAfterTime = '',
    isManualError = false,
  } = props;

  const key = `${label.toLowerCase()}_time`;
  const manualError =
    isManualError &&
    dataFormik?.errors?.[key] &&
    dataFormik?.touched?.[key] &&
    !dataFormik?.values?.[key]
      ? dataFormik?.errors?.[key]
      : false;
  const today = new Date();
  const hourInitial = today.getHours();
  const hourDefault =
    hourInitial.toString().length == 1 ? `0${hourInitial}` : hourInitial;
  const minuteInitial = Math.min(today.getMinutes() + 1, 59);
  const minuteDefault =
    minuteInitial.toString().length == 1 ? `0${minuteInitial}` : minuteInitial;

  const [showSelectBox, setShowSelectBox] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputHour, setInputHour] = useState('');
  const [inputMinute, setInputMinute] = useState('');

  const isAnytime = dataFormik?.values?.list_day?.some(
    (day) => day.list_day?.toLowerCase() === 'anytime',
  );

  useEffect(() => {
    if (value) {
      try {
        const split = value.split(':');
        setInputHour(split[0]);
        setInputMinute(split[1]);
        setInputValue(`${split[0]}:${split[1]}`);
      } catch (error) {
        setInputHour(hourDefault);
        setInputMinute(minuteDefault);
      }
    }
  }, [value]);

  const handleValue = () => {
    try {
      props.onChange(inputValue);
      setShowSelectBox(false);
    } catch (error) {
      setShowSelectBox(false);
    }
  };

  const handleChildValue = (value, isMinute) => {
    if (isMinute) {
      setInputMinute(value);
    } else {
      setInputHour(value);
    }
    const newValue = `${
      !isMinute ? value : inputHour ? inputHour : hourDefault
    }:${isMinute ? value : inputMinute ? inputMinute : minuteDefault}`;
    setInputValue(newValue);
    props.onChange(newValue);
  };

  const isDisabledTime = (value, isMinute = false) => {
    const timeIndex = isMinute ? 1 : 0;
    const isForMinute = Boolean(isMinute && inputHour != '');
    if (disableBeforeTime) {
      const beforeTime = disableBeforeTime.split(':');
      const isMoreHour = isForMinute
        ? parseInt(inputHour) >= parseInt(beforeTime[0])
        : true;
      if (
        isMinute
          ? isMoreHour && parseInt(beforeTime[timeIndex]) <= parseInt(value)
          : parseInt(beforeTime[timeIndex]) < parseInt(value)
      ) {
        return true;
      }
    }
    if (disableAfterTime) {
      const afterTime = disableAfterTime.split(':');
      const isLessHour = isForMinute
        ? parseInt(inputHour) <= parseInt(afterTime[0])
        : true;
      if (
        isMinute
          ? isLessHour && parseInt(afterTime[timeIndex]) >= parseInt(value)
          : parseInt(afterTime[timeIndex]) > parseInt(value)
      ) {
        return true;
      }
    }
    return false;
  };

  const checkValue = (item, isMinute) => {
    item = item ? item : `${isMinute ? minuteDefault : hourDefault}`;
    if (isMinute) {
      return inputMinute == item;
    } else {
      return inputHour == item;
    }
  };

  const OptionItem = ({ item, index, isMinute = false }) => {
    const itemActive =
      index == ((isMinute ? minuteOptions : hourOptions) || []).length - 1;
    const timeActive = checkValue(item, isMinute);
    const isDisable = isDisabledTime(item, isMinute);
    return (
      <TouchableOpacity
        testID={
          testID
            ? `${testID}-${isMinute ? 'minute' : 'hour'}-${item}`
            : undefined
        }
        activeOpacity={0.6}
        style={[
          styles.optionItem,
          {
            borderBottomColor: itemActive ? 'transparent' : Colors.grey0,
            backgroundColor: timeActive ? Colors.blue0 : Colors.white,
            opacity: isDisable ? 0.2 : 1,
          },
        ]}
        onPress={() => handleChildValue(item, isMinute)}
        disabled={isDisable}>
        <SociollaText
          style={{
            color: timeActive ? Colors.white : Colors.black,
          }}>
          {item}
        </SociollaText>
      </TouchableOpacity>
    );
  };

  return (
    <>
      <View testID={testID} style={[formControlContainer, containerStyle]}>
        <FormLabel {...props} />
        <FormOverlay
          {...props}
          onCallback={() =>
            isAnytime
              ? false
              : editable
              ? setShowSelectBox(!showSelectBox)
              : false
          }
        />
        <View
          style={[
            formControl,
            inputContainerStyle,
            hasError || manualError ? formControlError : {},
            !editable || isAnytime ? { backgroundColor: '#ebebeb' } : {},
            styles.formInput,
          ]}>
          {placeholder && !inputValue ? (
            <SociollaText style={[formInputText, styles.placeholder]}>
              {placeholder}
            </SociollaText>
          ) : (
            <SociollaText style={[formInputText, { alignSelf: 'flex-start' }]}>
              {inputValue}
            </SociollaText>
          )}
          <FormArrow {...props} right={5} />
        </View>
        <FormError
          {...{ ...props, hasError: manualError ? manualError : hasError }}
        />
      </View>
      <ModalHalf
        testID={testID}
        title={label}
        visible={showSelectBox}
        onClose={() => setShowSelectBox(false)}
        maxWidth={isWeb ? 150 : '100%'}
        spaceFromTop={isWeb ? '10%' : false}
        isScroll={false}>
        <ScrollView scrollEnabled={false} style={styles.scrollContainer}>
          <View style={styles.pickerContainer}>
            <View
              style={[
                styles.row,
                styles.timeLabelContainer,
                styles.containerTopBorder,
              ]}>
              <SociollaText style={styles.timeLabel} type="medium">
                Hour
              </SociollaText>
              <SociollaText style={styles.timeLabel} type="medium">
                Minute
              </SociollaText>
            </View>
            <View style={styles.row}>
              <ScrollView
                showsVerticalScrollIndicator={isWeb}
                style={styles.timeContainer}>
                {(hourOptions || []).map((item, idx) => (
                  <OptionItem key={idx} index={idx} item={item} />
                ))}
              </ScrollView>
              <ScrollView
                showsVerticalScrollIndicator={isWeb}
                style={[
                  styles.timeContainer,
                  { borderLeftColor: Colors.grey0, borderLeftWidth: 1 },
                ]}>
                {(minuteOptions || []).map((item, idx) => (
                  <OptionItem key={idx} index={idx} item={item} isMinute />
                ))}
              </ScrollView>
            </View>
            <View style={[styles.containerTopBorder, styles.buttonContainer]}>
              <Button
                testID={testID ? `${testID}-submit` : undefined}
                rounded={true}
                buttonStyle={styles.buttonSubmit}
                disabledStyle={styles.buttonSubmit}
                disabled={!inputHour || !inputMinute}
                title={
                  <SociollaText type="bold" style={styles.buttonText}>
                    SUBMIT
                  </SociollaText>
                }
                onPress={() => handleValue()}
              />
            </View>
          </View>
        </ScrollView>
      </ModalHalf>
    </>
  );
};

const styles = StyleSheet.create({
  row: { width: '100%', flexDirection: 'row' },
  containerTopBorder: { borderTopColor: Colors.grey0, borderTopWidth: 1 },
  timeLabelContainer: { borderBottomColor: Colors.grey0, borderBottomWidth: 1 },
  timeLabel: { width: '50%', fontSize: 13, padding: 10, textAlign: 'center' },
  timeContainer: { width: '50%', height: 215 },
  buttonSubmit: { width: '100%' },
  placeholder: {
    alignSelf: 'flex-start',
    color: '#90919b',
  },
  formInput: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'left',
    height: INPUT_HEIGHT,
  },
  scrollContainer: { width: '100%', height: 300 },
  pickerContainer: {
    position: isAndroid ? 'relative' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 300,
  },
  buttonContainer: { width: '100%', padding: 10 },
  buttonText: {
    fontSize: 14,
    letterSpacing: 0.5,
    color: Colors.white,
    lineHeight: 12,
  },
  optionItem: {
    width: '100%',
    paddingHorizontal: 15,
    paddingVertical: isWeb ? 13 : 17,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

TimePickerDefault.propTypes = propTypes;
TimePickerDefault.defaultProps = defaultProps;

export default React.memo(TimePickerDefault, (prev, next) => prev == next);
